"use client";

import {
  Container,
  Title,
  Text,
  Box,
  Stack,
  SimpleGrid,
  Group,
  Button,
  Anchor,
  Divider,
  Paper,
  ThemeIcon,
} from "@mantine/core";
import { IconArrowRight, IconCheck, IconX } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";

const topColors = [
  { color: "#FFFFFF", nameKey: "brightWhite", whyKey: "brightWhiteWhy" },
  { color: "#4169E1", nameKey: "royalBlue", whyKey: "royalBlueWhy" },
  { color: "#50C878", nameKey: "emeraldGreen", whyKey: "emeraldGreenWhy" },
  { color: "#9400D3", nameKey: "violet", whyKey: "violetWhy" },
  { color: "#FFD700", nameKey: "gold", whyKey: "goldWhy" },
  { color: "#FF7F50", nameKey: "coral", whyKey: "coralWhy" },
  { color: "#DC143C", nameKey: "crimsonRed", whyKey: "crimsonRedWhy" },
  { color: "#FF69B4", nameKey: "hotPink", whyKey: "hotPinkWhy" },
  { color: "#40E0D0", nameKey: "turquoise", whyKey: "turquoiseWhy" },
  { color: "#C0C0C0", nameKey: "silver", whyKey: "silverWhy" },
  { color: "#800020", nameKey: "burgundy", whyKey: "burgundyWhy" },
  { color: "#E0B0FF", nameKey: "mauve", whyKey: "mauveWhy" },
];

const warmUndertoneColors = [
  { color: "#FF7F50", nameKey: "warmCoral" },
  { color: "#FFD700", nameKey: "warmGold" },
  { color: "#FF4500", nameKey: "warmOrangeRed" },
  { color: "#228B22", nameKey: "warmForestGreen" },
  { color: "#40E0D0", nameKey: "warmTurquoise" },
];

const coolUndertoneColors = [
  { color: "#4169E1", nameKey: "coolRoyalBlue" },
  { color: "#9400D3", nameKey: "coolViolet" },
  { color: "#DC143C", nameKey: "coolTrueRed" },
  { color: "#FFFFFF", nameKey: "coolBrightWhite" },
  { color: "#50C878", nameKey: "coolEmerald" },
];

const neutralUndertoneColors = [
  { color: "#FF69B4", nameKey: "neutralHotPink" },
  { color: "#800020", nameKey: "neutralBurgundy" },
  { color: "#008080", nameKey: "neutralTeal" },
  { color: "#E0B0FF", nameKey: "neutralMauve" },
  { color: "#C0C0C0", nameKey: "neutralSilver" },
];

const colorsToAvoid = [
  { color: "#3D2314", nameKey: "brownsTooClose", whyKey: "brownsTooCloseWhy", insteadKey: "brownsTooCloseInstead" },
  { color: "#696969", nameKey: "muddyGrays", whyKey: "muddyGraysWhy", insteadKey: "muddyGraysInstead" },
  { color: "#8B8B7A", nameKey: "mutedKhaki", whyKey: "mutedKhakiWhy", insteadKey: "mutedKhakiInstead" },
  { color: "#D2B48C", nameKey: "tanBeige", whyKey: "tanBeigeWhy", insteadKey: "tanBeigeInstead" },
  { color: "#A9A9A9", nameKey: "dustyAshy", whyKey: "dustyAshyWhy", insteadKey: "dustyAshyInstead" },
];

function ColorSwatch({ color, size = 48 }: { color: string; size?: number }) {
  return (
    <Box
      style={{
        width: size,
        height: size,
        backgroundColor: color,
        borderRadius: 8,
        border: color === "#FFFFFF" ? "1px solid #e0e0e0" : "none",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      }}
    />
  );
}

export default function BestColorsForDarkSkinContent() {
  const t = useTranslations("bestColorsForDarkSkin");

  return (
    <Container size="md" py="xl">
      {/* HERO */}
      <Stack gap="xs" mb={48}>
        <Title order={1} size={36} lh={1.2}>
          {t("hero.title")}
        </Title>
        <Text size="xl" c="dimmed" maw={600}>
          {t("hero.subtitle")}
        </Text>
      </Stack>

      {/* SECTION 1: TOP 12 COLORS */}
      <Box mb={64}>
        <Title order={2} size={28} mb={32}>
          {t("topColors.title")}
        </Title>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={24}>
          {topColors.map((item) => (
            <Group key={item.nameKey} gap="md" align="flex-start">
              <ColorSwatch color={item.color} size={56} />
              <Box style={{ flex: 1 }}>
                <Text fw={600} size="lg">{t(`topColors.${item.nameKey}`)}</Text>
                <Text c="dimmed" size="sm">{t(`topColors.${item.whyKey}`)}</Text>
              </Box>
            </Group>
          ))}
        </SimpleGrid>

        <Paper withBorder p="lg" mt={40} radius="md" bg="gray.0">
          <Text size="md" mb="sm">
            {t("topColors.ctaText")}
          </Text>
          <Button
            component={Link}
            href="/ai-color-analysis"
            color="teal"
            rightSection={<IconArrowRight size={16} />}
          >
            {t("topColors.ctaButton")}
          </Button>
        </Paper>
      </Box>

      <Divider my={48} />

      {/* SECTION 2: VISUAL PROOF */}
      <Box mb={64}>
        <Title order={2} size={28} mb={16}>
          {t("visualProof.title")}
        </Title>

        <Text c="dimmed" mb={32} maw={700}>
          {t("visualProof.intro")}
        </Text>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={24} mb={32}>
          <Paper withBorder p="lg" radius="md">
            <Group gap="xs" mb="md">
              <ThemeIcon color="green" size="sm" radius="xl">
                <IconCheck size={14} />
              </ThemeIcon>
              <Text fw={600}>{t("visualProof.flatteringTitle")}</Text>
            </Group>
            <Group gap="sm" mb="md">
              <ColorSwatch color="#FFFFFF" size={40} />
              <ColorSwatch color="#4169E1" size={40} />
              <ColorSwatch color="#50C878" size={40} />
              <ColorSwatch color="#FFD700" size={40} />
            </Group>
            <Text size="sm" c="dimmed">
              {t("visualProof.flatteringText")}
            </Text>
          </Paper>

          <Paper withBorder p="lg" radius="md">
            <Group gap="xs" mb="md">
              <ThemeIcon color="red" size="sm" radius="xl">
                <IconX size={14} />
              </ThemeIcon>
              <Text fw={600}>{t("visualProof.unflatteringTitle")}</Text>
            </Group>
            <Group gap="sm" mb="md">
              <ColorSwatch color="#3D2314" size={40} />
              <ColorSwatch color="#696969" size={40} />
              <ColorSwatch color="#8B8B7A" size={40} />
              <ColorSwatch color="#D2B48C" size={40} />
            </Group>
            <Text size="sm" c="dimmed">
              {t("visualProof.unflatteringText")}
            </Text>
          </Paper>
        </SimpleGrid>

        <Paper p="lg" radius="md" bg="blue.0">
          <Text fw={600} mb="xs">{t("visualProof.whyTitle")}</Text>
          <Text size="sm">
            {t("visualProof.whyText")}
          </Text>
        </Paper>
      </Box>

      <Divider my={48} />

      {/* SECTION 3: BY UNDERTONE */}
      <Box mb={64}>
        <Title order={2} size={28} mb={32}>
          {t("byUndertone.title")}
        </Title>

        <Stack gap={40}>
          {/* Warm */}
          <Box>
            <Title order={3} size={22} mb={16}>
              {t("byUndertone.warmTitle")}
            </Title>
            <Text size="sm" c="dimmed" mb={16}>{t("byUndertone.warmIntro")}</Text>

            <Group gap="lg" mb={16}>
              {warmUndertoneColors.map((item) => (
                <Stack key={item.nameKey} gap={4} align="center">
                  <ColorSwatch color={item.color} size={48} />
                  <Text size="xs" ta="center">{t(`byUndertone.${item.nameKey}`)}</Text>
                </Stack>
              ))}
            </Group>

            <Text size="sm" c="dimmed">
              {t("byUndertone.warmWhy")}
            </Text>
          </Box>

          {/* Cool */}
          <Box>
            <Title order={3} size={22} mb={16}>
              {t("byUndertone.coolTitle")}
            </Title>
            <Text size="sm" c="dimmed" mb={16}>{t("byUndertone.coolIntro")}</Text>

            <Group gap="lg" mb={16}>
              {coolUndertoneColors.map((item) => (
                <Stack key={item.nameKey} gap={4} align="center">
                  <ColorSwatch color={item.color} size={48} />
                  <Text size="xs" ta="center">{t(`byUndertone.${item.nameKey}`)}</Text>
                </Stack>
              ))}
            </Group>

            <Text size="sm" c="dimmed">
              {t("byUndertone.coolWhy")}
            </Text>
          </Box>

          {/* Neutral */}
          <Box>
            <Title order={3} size={22} mb={16}>
              {t("byUndertone.neutralTitle")}
            </Title>
            <Text size="sm" c="dimmed" mb={16}>{t("byUndertone.neutralIntro")}</Text>

            <Group gap="lg" mb={16}>
              {neutralUndertoneColors.map((item) => (
                <Stack key={item.nameKey} gap={4} align="center">
                  <ColorSwatch color={item.color} size={48} />
                  <Text size="xs" ta="center">{t(`byUndertone.${item.nameKey}`)}</Text>
                </Stack>
              ))}
            </Group>

            <Text size="sm" c="dimmed">
              {t("byUndertone.neutralWhy")}
            </Text>
          </Box>
        </Stack>

        <Paper withBorder p="lg" mt={40} radius="md" bg="teal.0">
          <Text fw={600} mb="sm">
            {t("byUndertone.ctaText")}
          </Text>
          <Button
            component={Link}
            href="/ai-color-analysis"
            color="teal"
            size="md"
            rightSection={<IconArrowRight size={16} />}
          >
            {t("byUndertone.ctaButton")}
          </Button>
        </Paper>
      </Box>

      <Divider my={48} />

      {/* SECTION 4: COLORS TO AVOID */}
      <Box mb={64}>
        <Title order={2} size={28} mb={16}>
          {t("colorsToAvoid.title")}
        </Title>

        <Text c="dimmed" mb={32}>
          {t("colorsToAvoid.intro")}
        </Text>

        <Stack gap={20}>
          {colorsToAvoid.map((item) => (
            <Group key={item.nameKey} gap="md" align="flex-start">
              <ColorSwatch color={item.color} size={48} />
              <Box style={{ flex: 1 }}>
                <Group gap="xs">
                  <Text fw={600}>{t(`colorsToAvoid.${item.nameKey}`)}</Text>
                  <Text c="red.6" size="sm">— {t(`colorsToAvoid.${item.whyKey}`)}</Text>
                </Group>
                <Text size="sm" c="teal.7">
                  {t("colorsToAvoid.insteadLabel")} <strong>{t(`colorsToAvoid.${item.insteadKey}`)}</strong>
                </Text>
              </Box>
            </Group>
          ))}
        </Stack>
      </Box>

      <Divider my={48} />

      {/* SECTION 5: WHITE */}
      <Box mb={64}>
        <Title order={2} size={28} mb={16}>
          {t("whiteSection.title")}
        </Title>

        <Text c="dimmed" mb={24}>
          {t("whiteSection.intro")}
        </Text>

        <Paper p="lg" radius="md" bg="gray.1" mb={24}>
          <Text size="lg" ta="center">
            {t("whiteSection.quote")}
          </Text>
        </Paper>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={16}>
          <Paper withBorder p="md" radius="md">
            <Text fw={600} mb="xs">{t("whiteSection.brightWhiteTitle")}</Text>
            <Text size="sm" c="dimmed">
              {t("whiteSection.brightWhiteText")}
            </Text>
          </Paper>
          <Paper withBorder p="md" radius="md">
            <Text fw={600} mb="xs">{t("whiteSection.creamTitle")}</Text>
            <Text size="sm" c="dimmed">
              {t("whiteSection.creamText")}
            </Text>
          </Paper>
        </SimpleGrid>
      </Box>

      <Divider my={48} />

      {/* SECTION 6: SEASONAL */}
      <Box mb={64}>
        <Title order={2} size={28} mb={16}>
          {t("seasonalSection.title")}
        </Title>

        <Text c="dimmed" mb={8}>
          {t("seasonalSection.intro")}
        </Text>

        <Paper p="md" radius="md" bg="gray.1" mb={24}>
          <Text fw={600} size="lg" ta="center">
            {t("seasonalSection.quote")}
          </Text>
        </Paper>

        <Text mb={32}>
          {t("seasonalSection.explanation")}
        </Text>

        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing={16} mb={32}>
          <Anchor component={Link} href="/color-palette/winter/deep" underline="never">
            <Paper withBorder p="md" radius="md" ta="center" style={{ cursor: "pointer" }}>
              <Group gap={4} justify="center" mb="xs">
                <Box w={16} h={16} bg="#191970" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#800020" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#FFFFFF" style={{ borderRadius: 4, border: "1px solid #ddd" }} />
              </Group>
              <Text fw={600} size="sm">{t("seasonalSection.deepWinter")}</Text>
              <Text size="xs" c="dimmed">{t("seasonalSection.deepWinterSubtitle")}</Text>
            </Paper>
          </Anchor>

          <Anchor component={Link} href="/color-palette/autumn/deep" underline="never">
            <Paper withBorder p="md" radius="md" ta="center" style={{ cursor: "pointer" }}>
              <Group gap={4} justify="center" mb="xs">
                <Box w={16} h={16} bg="#8B4513" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#228B22" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#FFD700" style={{ borderRadius: 4 }} />
              </Group>
              <Text fw={600} size="sm">{t("seasonalSection.deepAutumn")}</Text>
              <Text size="xs" c="dimmed">{t("seasonalSection.deepAutumnSubtitle")}</Text>
            </Paper>
          </Anchor>

          <Anchor component={Link} href="/color-palette/winter/cool" underline="never">
            <Paper withBorder p="md" radius="md" ta="center" style={{ cursor: "pointer" }}>
              <Group gap={4} justify="center" mb="xs">
                <Box w={16} h={16} bg="#FF1493" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#00BFFF" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#FFFFFF" style={{ borderRadius: 4, border: "1px solid #ddd" }} />
              </Group>
              <Text fw={600} size="sm">{t("seasonalSection.brightWinter")}</Text>
              <Text size="xs" c="dimmed">{t("seasonalSection.brightWinterSubtitle")}</Text>
            </Paper>
          </Anchor>

          <Anchor component={Link} href="/color-palette/spring/bright" underline="never">
            <Paper withBorder p="md" radius="md" ta="center" style={{ cursor: "pointer" }}>
              <Group gap={4} justify="center" mb="xs">
                <Box w={16} h={16} bg="#FF7F50" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#40E0D0" style={{ borderRadius: 4 }} />
                <Box w={16} h={16} bg="#FFD700" style={{ borderRadius: 4 }} />
              </Group>
              <Text fw={600} size="sm">{t("seasonalSection.brightSpring")}</Text>
              <Text size="xs" c="dimmed">{t("seasonalSection.brightSpringSubtitle")}</Text>
            </Paper>
          </Anchor>
        </SimpleGrid>

        <Paper p="xl" radius="md" bg="teal.6" c="white" ta="center">
          <Text size="lg" fw={600} mb="sm">
            {t("seasonalSection.ctaTitle")}
          </Text>
          <Text size="sm" mb="md" opacity={0.9}>
            {t("seasonalSection.ctaSubtitle")}
          </Text>
          <Button
            component={Link}
            href="/ai-color-analysis"
            color="teal"
            variant="white"
            size="lg"
            rightSection={<IconArrowRight size={18} />}
          >
            {t("seasonalSection.ctaButton")}
          </Button>
        </Paper>
      </Box>

      <Divider my={48} />

      {/* SECTION 7: FAQ */}
      <Box mb={48}>
        <Title order={2} size={28} mb={32}>
          {t("faq.title")}
        </Title>

        <Stack gap={24}>
          <Box>
            <Text fw={600} mb={4}>{t("faq.q1")}</Text>
            <Text c="dimmed">
              {t("faq.a1")}
            </Text>
          </Box>

          <Box>
            <Text fw={600} mb={4}>{t("faq.q2")}</Text>
            <Text c="dimmed">
              {t("faq.a2")}
            </Text>
          </Box>

          <Box>
            <Text fw={600} mb={4}>{t("faq.q3")}</Text>
            <Text c="dimmed">
              {t("faq.a3")}
            </Text>
          </Box>

          <Box>
            <Text fw={600} mb={4}>{t("faq.q4")}</Text>
            <Text c="dimmed">
              {t("faq.a4")}
            </Text>
          </Box>

          <Box>
            <Text fw={600} mb={4}>{t("faq.q5")}</Text>
            <Text c="dimmed">
              {t("faq.a5")}
            </Text>
          </Box>
        </Stack>
      </Box>
    </Container>
  );
}
